import { useState, useEffect } from 'react';
import { IconCopy, IconCheck, IconSettings } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';

export function ColumnToCsvPage() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [addQuotes, setAddQuotes] = useState(true);
  const [addSpaces, setAddSpaces] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!input.trim()) {
      setOutput('');
      return;
    }

    const lines = input.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    const result = lines.map(line => {
      const trimmed = line.trim();
      if (addQuotes) {
        return `"${trimmed}"`;
      }
      return trimmed;
    }).join(addSpaces ? ', ' : ',');

    setOutput(result);
  }, [input, addQuotes, addSpaces]);

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Copy failed
    }
  };

  return (
    <div>
      <PageHeader
        title="Column to Comma-Separated List"
        subtitle="Convert a column of values into a comma-separated list"
      />

      <MainCard>
        <div className="flex flex-col gap-6">
          {/* Settings */}
          <div className="flex items-center gap-4 flex-wrap rounded-lg bg-muted/50 p-4">
            <IconSettings className="size-5 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <Checkbox
                id="addQuotes"
                checked={addQuotes}
                onCheckedChange={(checked) => setAddQuotes(checked as boolean)}
              />
              <Label htmlFor="addQuotes">Add quotes around values</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="addSpaces"
                checked={addSpaces}
                onCheckedChange={(checked) => setAddSpaces(checked as boolean)}
              />
              <Label htmlFor="addSpaces">Add spaces after commas</Label>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Column of Values</p>
            <Textarea
              rows={10}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={"Enter one value per line\nValue 1\nValue 2\nValue 3"}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Enter one value per line
            </p>
          </div>

          {/* Output */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium">Comma-Separated List</p>
              <Button
                onClick={handleCopy}
                disabled={!output}
                variant="outline"
                size="sm"
              >
                {copied ? <IconCheck className="size-4" /> : <IconCopy className="size-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <Textarea
              rows={7}
              value={output}
              readOnly
              placeholder="Comma-separated list will appear here..."
              className="font-mono text-sm bg-muted/50"
            />
          </div>
        </div>
      </MainCard>
    </div>
  );
}
